<template>
  <div class="duration-input">
    <input
      ref="hoursRef"
      type="text"
      v-model="maskedValue"
      @click="onClick"
      @keydown="onKeydown"
      @blur="onBlur"
      @selectionchange="onSelectionChange"
      :placeholder="props.defaultValue"
      data-component-element-id="duration-input"
      v-bind="$attrs"
    />
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';

const MASK_DEFAULT = '__:__:__';
const emit = defineEmits([
  'blur',
  'click'
]);

defineOptions({
  inheritAttrs: false
});

const props = defineProps({
  defaultValue: {
    type: String,
    default: '00:00:10'
  }
});

const modelValue = defineModel({
  modelValue: {
    type: String,
    default: '00:00:10'
  }
});

const CONTROL_KEYS = [
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'CapsLock',
  'Tab',
  'ArrowLeft',
  'ArrowUp',
  'ArrowDown',
  'Insert',
  'Home',
  'End',
  'PageUp',
  'PageDown'
];

const MODIFIER_KEYS = [
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'Fn',
  'Hyper',
  'Super',
  'OS',
  'CapsLock',
  'Win'
];

// This is intentional to keep the initial value static (e.g. not reactive)
// so that fallback value is always the same.
const initialValue = ref(modelValue.value ?? props.defaultValue);

const maskedValue = ref(MASK_DEFAULT);

const hours = ref('00');
const minutes = ref('00');
const seconds = ref('10');

const segmentsMap = {
  hours,
  minutes,
  seconds
};

const segmentOffsets = {
  hours: [0, 2],
  minutes: [3, 4],
  seconds: [6, 7]
}

// This needed to improve UX on second Backspace key press
const prevKey = ref(null);

// Independent state from Input.selection* properties to
// track components' intentions to the selection range
const currentSelection = ref({ selectionStart: 0, selectionEnd: 0, selectionDirection: 'forward' })

// This is the sequence of segments in the input mask, important to
// simplify navigation on Arrows Right/Left and Backspace/Delete keys
const segmentsSequence = ['hours', 'minutes', 'seconds'];

// Lookup for the previous and next segments
const prevSegment = (currentSegment) => segmentsSequence.at(
  segmentsSequence.indexOf(currentSegment) - 1) ?? segmentsSequence[0];
const nextSegment = (currentSegment) => segmentsSequence.at(
  segmentsSequence.indexOf(currentSegment) + 1) ?? currentSegment;

const cleanup = (value) => value.replace(/_/g, '0').toString().padStart(2, '0');
const normalizeMaskedValue = () => `${cleanup(hours.value)}:${cleanup(minutes.value)}:${cleanup(seconds.value)}`;
const writeSlot = (s, value, position) => s.slice(0, position) + value + s.slice(position + 1);

// NOTE: This is the crutual part of the component functionality.
// Here we identify selected segments based on the input selection.
const getSelectedSegments = () => {
  const { selectionStart, selectionEnd, selectionDirection } = currentSelection.value;
  const segments = [];

  if (Math.max(selectionStart, 0) <= Math.min(selectionEnd, 2)) {
    segments.push('hours');
  }
  if (Math.max(selectionStart, 3) <= Math.min(selectionEnd, 5)) {
    segments.push('minutes');
  }
  if (Math.max(selectionStart, 5) <= Math.min(selectionEnd, 9)) {
    segments.push('seconds');
  }

  // Reverse order of segments for backward selection direction
  if (selectionDirection === 'backward') {
    segments.reverse();
  }

  return segments;
}

// This function is used to set the selection range on the input element after Vue next tick
const setSelection = (target, start, end) => nextTick(() => target.setSelectionRange(start, end));

// Focus on the segment leveraging offsets
const focusSegment = (target, segment) => {
  const [offsetStart, offsetEnd] = segmentOffsets[segment];
  setSelection(target, offsetStart, offsetEnd + 1);
}

// Erase list of segments
const eraseSegments = (segments) => {
  segments.forEach((segment) => {
    segmentsMap[segment].value = '__';
  });
}

// check that there's no Ctrl/Alt/Shift/Meta + <KEY> pressed
const isModifierKeyActive = (event) => MODIFIER_KEYS.some(key => event.getModifierState(key));

const onKeydown = ($event) => {
  // add handler to the queue
  const { selectionStart, selectionEnd } = currentSelection.value;
  const target = $event.target;
  const segments = getSelectedSegments();
  const targetSegment = segments[0] ?? null;

  if (!targetSegment) return;

  const [offsetStart, offsetEnd] = segmentOffsets[targetSegment];
  const position = selectionStart > offsetStart ? 1 : 0;

  // Usecase 1: User press Delete key, currently focused slot replaced with '_'
  // Usecase 2: User press Backspace key, currently focused in position 1, then whole segment replaced with '__'
  if ($event.key === 'Delete') {
    $event.preventDefault();
    // For more than one slot selected, replace all of them with '__'

    if (segments.length > 1) {
      eraseSegments(segments);
    } else
      if (selectionEnd - selectionStart > 1) {
        eraseSegments([targetSegment]);
      } else {
        segmentsMap[targetSegment].value = writeSlot(segmentsMap[targetSegment].value, '_', position)
      }

    // NOTE: it's important to do it before setting new selection
    maskedValue.value = `${hours.value}:${minutes.value}:${seconds.value}`;
    if (segmentsMap[targetSegment].value === '__') {
      focusSegment(target, targetSegment);
    }
  } else
    if ($event.key === 'Backspace') {
      // Usecase 3: User press Backspace key, currently focused in position 0, then whole segment replaced with '__'
      // Usecase 4: User press Backspace key *second time*, currently focused in position 0, then previous segment takes focus
      $event.preventDefault();
      if (segments.length > 1) {
        eraseSegments([targetSegment]);
      } else
        if (selectionStart === offsetStart) {
          eraseSegments([targetSegment]);
        } else {
          segmentsMap[targetSegment].value = writeSlot(segmentsMap[targetSegment].value, '_', position)
        }

      // NOTE: it's important to do it before setting new selection
      maskedValue.value = `${hours.value}:${minutes.value}:${seconds.value}`;

      if (position === 0 && selectionStart <= offsetStart && prevKey.value === 'Backspace') {
        focusSegment(target, prevSegment(targetSegment));
      } else {
        setSelection(target, offsetStart, offsetStart + 1);
      }
    } else
      if ($event.key === 'ArrowRight') {
        // Usecase 5: User press ArrowRight key, currently focused in position 1, then next segment takes focus
        const { selectionStart } = currentSelection.value;

        if (selectionStart >= offsetEnd) {
          focusSegment(target, nextSegment(targetSegment));
          $event.preventDefault();
        }
      } else
        if (/^[0-9]$/.test($event.key)) {
          // Usecase 6: User press a digit key, currently focused in position 1, then next segment takes focus
          // Usecase 7: User press a digit key, currently focused in position 0, then next slot gets selected
          $event.preventDefault();
          const value = segmentsMap[targetSegment].value;
          let newValue = writeSlot(value, $event.key, position);
          const inputNumber = parseInt(newValue, 10);

          if (targetSegment === 'hours' && inputNumber > 23) {
            newValue = '23';
          } else if (inputNumber > 59) {
            newValue = '59';
          }

          segmentsMap[targetSegment].value = newValue;

          // NOTE: it's important to do it before setting new selection
          maskedValue.value = `${hours.value}:${minutes.value}:${seconds.value}`;

          if (position === 1) {
            const newSegment = nextSegment(targetSegment);
            const offsetStart = segmentOffsets[newSegment][0];
            setSelection(target, offsetStart, offsetStart + 2);
          } else {
            setSelection(target, offsetStart + 1, offsetStart + 2);
          }
        } else
          // Usecase 8: User press any other key than CONTROL_KEYS *AND* no modifiers
          if (!CONTROL_KEYS.includes($event.key) && !isModifierKeyActive($event)) {
            // DEFAULT
            maskedValue.value = `${hours.value}:${minutes.value}:${seconds.value}`;
            focusSegment(target, targetSegment);
            $event.preventDefault();
          }

  prevKey.value = $event.key;
}

// Replace '_' with '0' on blur
const onBlur = () => {
  hours.value = hours.value.replace('_', '0')
  minutes.value = minutes.value.replace('_', '0')
  seconds.value = seconds.value.replace('_', '0')

  // Ensure minimum duration of 10 seconds regardless of defaultValue
  if (maskedValue.value?.replace(/[0]/g, '_') === '__:__:__') {
    if (!modelValue.value) {
      const parts = props.defaultValue.split(':');
      hours.value = parts[0] ?? '00';
      minutes.value = parts[1] ?? '00';
      seconds.value = parts[2] ?? '10';
    } else {
      // Note: fallback to initial value if modelValue is set
      maskedValue.value = initialValue.value;
      updateSegments(initialValue.value);
    }
  }

  maskedValue.value = normalizeMaskedValue()
  nextTick(() => {
    modelValue.value = maskedValue.value;
    emit('blur', maskedValue.value);
  });
}

// Track selection changes, since it's indepentent from the input
const onSelectionChange = (event) => {
  const target = event.target;
  currentSelection.value = {
    selectionStart: target.selectionStart,
    selectionEnd: target.selectionEnd,
    selectionDirection: target.selectionDirection
  }
}

const parseTimestamp = (time) => {
  const parts = time.split(':');
  return {
    hours: parts[0]?.padStart(2, '0') ?? '00',
    minutes: parts[1]?.padStart(2, '0') ?? '00',
    seconds: parts[2]?.padStart(2, '0') ?? '00'
  };
}

const updateSegments = (newValue) => {
  const { hours: hoursParsed, minutes: minutesParsed, seconds: secondsParsed } = parseTimestamp(newValue);
  hours.value = hoursParsed;
  minutes.value = minutesParsed;
  seconds.value = secondsParsed;
  maskedValue.value = normalizeMaskedValue();
};

const onClick = () => emit('click', maskedValue.value);

watch(() => modelValue.value, (newValue) => {
  if (!newValue) {
    return
  }

  if (newValue && !newValue.match(/^\w{2}[:]\w{2}[:]\w{2}$/)) {
    maskedValue.value = MASK_DEFAULT;
  } else if (newValue === '00:00:00') {
    modelValue.value = props.defaultValue;
    maskedValue.value = props.defaultValue;
    updateSegments(props.defaultValue);
  } else if (newValue !== maskedValue.value) {
    updateSegments(newValue);
  }
});

onMounted(() => {
  if (modelValue.value && modelValue.value.match(/^\w{2}[:]\w{2}[:]\w{2}$/)) {
    updateSegments(modelValue.value);
  } else {
    maskedValue.value = MASK_DEFAULT;
  }
});

</script>

<style lang="scss" scoped>
@reference '#tailwind.scss';

input {
  @apply border border-[#DCDFE5] box-border text-black text-inherit text-center w-[75px] py-1 px-[0.4rem] rounded-sm;
  outline: none;
}
</style>
